import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchItemDetails, fetchRecommendations, fetchUserProgress, likeItem, dislikeItem, voteForPaper } from '../../utils/api';
import PaperCard from './PaperCard';
import { Tabs, TabList, TabPanels, Tab, TabPanel, Heading, Text, Box, Spinner } from '@chakra-ui/react';

const SearchPage = () => {
  const [currentItem, setCurrentItem] = useState(null);
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userProgress, setUserProgress] = useState({ num_items_seen: 0, num_items_total: 229 });
  const [noRecommendations, setNoRecommendations] = useState(false);
  const [isSwipingRight, setIsSwipingRight] = useState(false);
  const [isSwipingLeft, setIsSwipingLeft] = useState(false);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [showInfoText, setShowInfoText] = useState(true); // State to control visibility of the info text
  const [isCardLoading, setIsCardLoading] = useState(false);

  const dates = useMemo(() => ["All","Jun 5", "Jun 6", "Jun 7"], []);
  const exclusions = useMemo(() => [
    [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,35,36,37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54,55,56,57,58,59,60,61,62,63,64,65,66,67,68,69,70,71,72,73,74,75,76,77,78,79,80,81,82,83,84,85,86,87,88,89,90,91,92,93,94,95,112,113,114,115,116,117,118,119,120,121,122,123,124,125,126,127,128,129,130,131,132,133,134,135,136,137,138,139,140,141,142,143,144,145,146,147,148,149,150,151,152,153,154,155,156,157,158,159,160,161,162,163,164,165,166,167,168,169,170,171,172,173,174,175,176,177,178,179,180,181,182,183,184,185,186,187,188,189,190,191,192,193,194,195,196,197,198,199,200,201,202,203,204,205,206,207,208,209,210,211,212,213,214,215,216,217,218,219,220,221,222,223,224,225,226,227,228, 23, 29],
    [0,1,2,3,4,5,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,68,69,70,71,72,73,74,75,76,77,78,79,80,81,82,83,84,85,86,87,88,89,90,91,92,93,94,95,96,97,98,99,100,101,102,103,104,105,106,107,108,109,110,111,160,161,162,163,164,165,166,167,41,216,228, 35, 44, 52, 60],
    [0,1,2,3,4,5,6,7,8,9,10,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54,55,56,57,58,59,60,61,62,63,64,65,66,67,96,97,98,99,100,101,102,103,104,105,106,107,108,109,110,111,112,113,114,115,116,117,118,119,120,121,122,123,124,125,126,127,128,129,130,131,132,133,134,135,136,137,138,139,140,141,142,143,144,145,146,147,148,149,150,151,152,153,154,155,156,157,158,159,168,169,170,171,172,173,174,175,176,177,178,179,180,181,182,183,184,185,186,187,188,189,190,191,192,193,194,195,196,197,198,199,200,201,202,203,204,205,206,207,208,209,210,211,212,213,214,215,216,217,218,219,220,221,222,223,224,225,226,227,228, 68, 77, 87],
    [41, 216, 228, 23, 29, 35, 44, 52, 60, 68 , 77, 87]
  ], []);

  const updateUserProgress = async () => {
    const progress = await fetchUserProgress();
    setUserProgress(progress);
  };

  const fetchAndDisplayItem = useCallback(async (excludedPapers) => {
    try {
      const response = await fetchRecommendations(excludedPapers); // Pass excluded papers

      console.log('Recommendations Response:', response);

      if (response.warning) {
        console.warn(response.warning);
        setNoRecommendations(true);
        setCurrentItem(null);
        setIsLoading(false);
        return;
      }

      const itemId = response.recommendation?.item_id;
      if (itemId !== null && itemId !== undefined) {
        console.log('Fetching details for item:', itemId);
        const itemDetails = await fetchItemDetails(itemId);
        setCurrentItem(itemDetails);
        setNoRecommendations(false);
      } else {
        console.error("No valid recommendations found or invalid response format");
        setNoRecommendations(true);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch item:", error);
      navigate("/login");
      setIsLoading(false);
    }
  }, [navigate]);

  const getExcludedItems = useCallback(() => {
    if (dates.length > 0) {
      if (dates[selectedDateIndex] === 'All') {
        return exclusions[exclusions.length - 1]; // Use the last entry in exclusions for "All"
      } else {
        return exclusions[selectedDateIndex - 1]; // Adjusting for "All" at index 0
      }
    }
    return [];
  }, [selectedDateIndex, dates, exclusions]);
  
  useEffect(() => {
    const initializeData = async () => {
      const excludedItems = getExcludedItems();
      await fetchAndDisplayItem(excludedItems);
      updateUserProgress();
    };
    initializeData();
  }, [selectedDateIndex, dates, fetchAndDisplayItem, getExcludedItems]);
  
  const handleLike = async () => {
    if (currentItem?.id !== null && currentItem?.id !== undefined) {
      setIsSwipingRight(true);
      setIsCardLoading(true);
      await likeItem(currentItem.id);

      await voteForPaper(currentItem.id);

      setTimeout(async () => {
        setShowInfoText(false); // Hide the info text
        await fetchAndDisplayItem(getExcludedItems());
        setIsSwipingRight(false);
        setIsCardLoading(false);
      }, 10);

      setUserProgress(prev => ({ ...prev, num_items_seen: prev.num_items_seen + 1 }));
    }
  };

  const handleDislike = async () => {
    if (currentItem?.id !== null && currentItem?.id !== undefined) {
      setIsSwipingLeft(true);
      setIsCardLoading(true);
      await dislikeItem(currentItem.id);
      setTimeout(async () => {
        setShowInfoText(false); // Hide the info text
        await fetchAndDisplayItem(getExcludedItems());
        setIsSwipingLeft(false);
        setIsCardLoading(false);
      }, 10);
      setUserProgress(prev => ({ ...prev, num_items_seen: prev.num_items_seen + 1 }));
    }
  };

  return (
    <div className="discover-page">
      <div className="paper-page-container">
        <Heading 
          as="h1" 
          size="xl" 
          className="page-title" 
          style={{
            background: 'linear-gradient(45deg, #E27A47 45%, #4BB19D 55%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}
        >
          Discover
        </Heading>
        <Box mt="20px">
          <Tabs isFitted variant="enclosed" index={selectedDateIndex} onChange={(index) => setSelectedDateIndex(index)}>
            <TabList className="tab-list" style={{ position: 'fixed', top: '80px', width: '100%', zIndex: 1000 }}>
              {dates.map((date, index) => (
                <Tab key={index} className={`tab ${selectedDateIndex === index ? 'tab-selected' : ''}`} fontSize="sm">
                  {date}
                </Tab>
              ))}
            </TabList>
            <Box pt="30px">
              <TabPanels>
                {dates.map((date, index) => (
                  <TabPanel key={index}>
                    {isCardLoading ? (
                      <Box textAlign="center" py={6}>
                        <Spinner size="xl" />
                      </Box>
                    ) : (
                      <>
                        {!isLoading && currentItem && !noRecommendations && (
                          <>
                            <PaperCard
                              title={currentItem.title}
                              authors={currentItem.authors}
                              keywords={currentItem.keywords}
                              abstract={currentItem.abstract}
                              onAdd={handleLike}
                              onSkip={handleDislike}
                              showInstructions={showInstructions}
                              remainingVotes={3}
                              showAddButton={true}
                              isSwipingRight={isSwipingRight}
                              isSwipingLeft={isSwipingLeft}
                              date={currentItem.date}
                              time_start={currentItem.time_start}
                              time_end={currentItem.time_end}
                              location={currentItem.location}
                            />
                            {showInfoText && (
                              <Text fontSize="xs" color="gray.300" textAlign="center" mt={2} mb={4} mx="auto" maxWidth="90%">
                                The recommender automatically learns your preferences based on your likes and dislikes
                              </Text>
                            )}
                          </>
                        )}
                        {!isLoading && noRecommendations && (
                          <div className="no-recommendations">
                            <p>All items have been discovered.</p>
                          </div>
                        )}
                      </>
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Box>
          </Tabs>
        </Box>
      </div>
      <button
        className={`help-button ${showInstructions ? 'help-button-pressed' : ''}`}
        onClick={() => setShowInstructions(prev => !prev)}
      >
        ?
      </button>
      <div className="progress-container">
        <div className="progress-bar" style={{ width: `${(userProgress.num_items_seen / 214) * 100}%` }}></div>
        <span className="progress-text">{`${userProgress.num_items_seen} / 214`}</span>
      </div>
    </div>
  );
  };
  
  export default SearchPage;
  